/**
 * Number tweening.
 *
 * A wage figure that changes because a shift just moved should be SEEN to
 * move: the eye reads a number rolling from 4,763 to 5,003 as "that drop cost
 * $240", where a number that is simply replaced reads as a different screen.
 * Half a second on an ease-out is long enough to register and short enough
 * never to lag a second drop.
 *
 * Interrupted mid-tween, the next tween starts from wherever the number was,
 * not from where it was going, so rapid changes glide rather than jump.
 * Under `prefers-reduced-motion` the value is applied directly.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './motion.ts';

export function useTween(target: number, ms = 520): number {
  const [value, setValue] = useState(target);
  const current = useRef(target);
  const raf = useRef(0);

  useEffect(() => {
    if (!Number.isFinite(target)) return;
    if (prefersReducedMotion()) {
      current.current = target;
      setValue(target);
      return;
    }
    const begin = current.current;
    const delta = target - begin;
    if (delta === 0) return;
    const start = performance.now();
    cancelAnimationFrame(raf.current);
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = begin + delta * eased;
      current.current = v;
      setValue(v);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, ms]);

  return value;
}
